import React, { useEffect, useState } from 'react';
import { Bell, CheckCircle2, Clock, Mail } from 'lucide-react';
import { getOrgInvitations, inviteMember } from '../api/api';
import { useAuth } from '../context/AuthContext';
import StyledSelect from '../components/StyledSelect';

const ROLES = ['admin', 'finance_manager', 'accountant', 'project_manager', 'employee', 'viewer'];
const CARD = { borderColor: '#f4d6df', background: '#fff' };
const PINK = '#c44569';

function statusBadge(status) {
  const map = {
    pending:  { bg: '#fef9c3', color: '#a16207' },
    accepted: { bg: '#dcfce7', color: '#15803d' },
    expired:  { bg: '#f1f5f9', color: '#64748b' },
  };
  const s = map[status] || map.pending;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.08em]" style={s}>
      {status || 'pending'}
    </span>
  );
}

export default function OrgAdminInvitationsPage() {
  const { activeOrg, canViewUsers, canInviteUsers } = useAuth();
  const [rows, setRows]           = useState([]);
  const [email, setEmail]         = useState('');
  const [role, setRole]           = useState('employee');
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const load = async () => {
    if (!activeOrg?.id) return;
    setLoading(true);
    try {
      const res = await getOrgInvitations(activeOrg.id);
      setRows(res?.data?.data?.invitations || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load invitations.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (canViewUsers || canInviteUsers) load();
    else setLoading(false);
  }, [activeOrg?.id, canViewUsers, canInviteUsers]);

  const onInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required.'); return; }
    setSubmitting(true); setError(''); setSuccess('');
    try {
      await inviteMember(activeOrg.id, { email: email.trim(), org_role: role });
      setSuccess(`Invitation sent to ${email.trim()}.`);
      setEmail('');
      setRole('employee');
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send invitation.');
    } finally { setSubmitting(false); }
  };

  if (!canViewUsers && !canInviteUsers) {
    return (
      <div className="rounded-3xl border p-8 text-center" style={CARD}>
        <Bell size={28} className="mx-auto mb-3" style={{ color: PINK }} />
        <p className="font-bold text-slate-900">Access Restricted</p>
        <p className="text-sm text-slate-500 mt-1">You do not have permission to manage invitations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-10">
      <div className="rounded-3xl border p-5 shadow-sm" style={CARD}>
        <h1 className="text-xl font-black text-slate-900">Invitations</h1>
        <p className="text-sm text-slate-500 mt-1">Invite new members to join {activeOrg?.name || 'your organization'}.</p>
      </div>

      {error ? <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: '#f9b8ca', background: '#fce0e8', color: PINK }}>{error}</div> : null}
      {success ? <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: '#a7f3d0', background: '#ecfdf5', color: '#047857' }}>{success}</div> : null}

      {canInviteUsers ? (
        <form onSubmit={onInvite} className="rounded-3xl border p-5 shadow-sm space-y-4" style={CARD}>
          <p className="font-bold text-slate-900 text-sm">Send New Invitation</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@email.com"
                required
                className="w-full rounded-xl border pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-rose-200"
                style={{ borderColor: '#f4d6df' }}
              />
            </div>
            <div className="w-full sm:w-56">
              <StyledSelect
                value={role}
                onChange={setRole}
                options={ROLES.map((r) => ({ value: r, label: r.replace(/_/g, ' ') }))}
                placeholder="Select role"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white shrink-0 disabled:opacity-60 hover:opacity-90 transition"
              style={{ background: PINK }}
            >
              {submitting ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      ) : null}

      <div className="rounded-3xl border overflow-hidden shadow-sm" style={CARD}>
        {loading ? (
          <p className="text-sm text-slate-400 p-6 text-center">Loading invitations…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 p-6 text-center">No invitations found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-[0.12em] border-b" style={{ borderColor: '#f4d6df' }}>
                  <th className="py-3 px-5">Email</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Expires</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-b last:border-0 hover:bg-slate-50" style={{ borderColor: '#f9f0f3' }}>
                    <td className="py-3 px-5 font-medium text-slate-800">{r.email}</td>
                    <td className="py-3 px-4">
                      <span className="text-xs text-slate-600">{(r.org_role || 'employee').replace(/_/g, ' ')}</span>
                    </td>
                    <td className="py-3 px-4">{statusBadge(r.status)}</td>
                    <td className="py-3 px-4 text-xs text-slate-400 flex items-center gap-1">
                      <Clock size={12} />
                      {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
